import React, { useState } from 'react';
import { Box, ToggleButton, Typography, useTheme } from '@mui/material';
import MonacoEditor from '@monaco-editor/react';
import Button from './Button';
import ToggleButtonGroup from './ToggleButtonGroup';
import CustomIframe from './CustomIframe';
import { useTranslation } from 'react-i18next';

const CustomHtmlContentEditor = ({ content = '', onChange }) => {
  const { t } = useTranslation(['lessons', 'common']);
  
  const PREVIEW_WIDTHS = {
    desktop: {
      label: t('lessons:editor.html.desktop'),
      width: '100%',
      maxWidth: 1344,
    },
    tablet: { label: t('lessons:editor.html.tablet'), width: 768 },
    mobile: { label: t('lessons:editor.html.mobile'), width: 375 },
  };
  
  const [showPreview, setShowPreview] = useState(false);
  const [previewMode, setPreviewMode] = useState('desktop');
  const theme = useTheme();

  const handleEditorChange = (newValue) => {
    onChange(newValue || '');
  };

  const handlePreviewMode = (event, newMode) => {
    if (newMode) {
      setPreviewMode(newMode);
    }
  };

  const togglePreview = () => {
    setShowPreview(!showPreview);
  };

  const previewStyle = {
    width:
      typeof PREVIEW_WIDTHS[previewMode].width === 'number'
        ? PREVIEW_WIDTHS[previewMode].width
        : '100%',
    maxWidth: PREVIEW_WIDTHS[previewMode].maxWidth || '100%',
    margin: '0 auto',
  };

  const monacoTheme = theme.palette.mode === 'dark' ? 'vs-dark' : 'light';

  return (
    <Box sx={{ width: '100%', mt: 2 }}>
      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
        {t('lessons:editor.customHtml.title')}
      </Typography>
      <MonacoEditor
        height="400px"
        defaultLanguage="html"
        language="html"
        value={content}
        onChange={handleEditorChange}
        options={{
          minimap: { enabled: false },
          fontSize: 14,
          scrollBeyondLastLine: false,
          wordWrap: 'on',
        }}
        theme={monacoTheme}
      />
      <Button variant="outlined" sx={{ mt: 2 }} onClick={togglePreview}>
        {showPreview
          ? t('lessons:editor.html.hidePreview')
          : t('lessons:editor.html.showPreview')}
      </Button>

      {showPreview && (
        <>
          <Typography variant="h6" sx={{ my: 2 }}>
            {t('lessons:editor.html.previewTitle')}
          </Typography>
          <ToggleButtonGroup
            value={previewMode}
            exclusive
            onChange={handlePreviewMode}
            size="small"
            color="primary"
            fullWidth
            sx={{
              m: '0 auto 10px',
              '& .MuiToggleButton-root.Mui-selected': { color: 'text.primary' },
            }}
          >
            <ToggleButton value="desktop">
              {t('lessons:editor.html.desktop')}
            </ToggleButton>
            <ToggleButton value="tablet">
              {t('lessons:editor.html.tablet')}
            </ToggleButton>
            <ToggleButton value="mobile">
              {t('lessons:editor.html.mobile')}
            </ToggleButton>
          </ToggleButtonGroup>

          <Box sx={previewStyle}>
            <Box
              sx={{
                border:
                  previewMode !== 'desktop'
                    ? `1px dashed ${theme.palette.divider}`
                    : 'none',
              }}
            >
              <CustomIframe
                srcDoc={content || ''}
                title="Custom HTML Preview"
                style={{
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  backgroundColor: '#fff',
                }}
                sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals"
              />
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default CustomHtmlContentEditor;
